import { isoToLocalDate } from '@/utils/dateRangePicker';

/** Enumerate inclusive YYYY-MM-DD dates between start and end (local calendar). */
export function enumerateDatesInRange(startDate: string, endDate: string): string[] {
  const start = isoToLocalDate(startDate);
  const end = isoToLocalDate(endDate);
  if (!start || !end || start > end) return [];

  const dates: string[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    const year = cursor.getFullYear();
    const month = `${cursor.getMonth() + 1}`.padStart(2, '0');
    const day = `${cursor.getDate()}`.padStart(2, '0');
    dates.push(`${year}-${month}-${day}`);
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

/** Concise axis label, e.g. "Aug 1". */
export function formatPerformanceAxisDate(isoDate: string): string {
  const date = isoToLocalDate(isoDate);
  if (!date) return isoDate;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(date);
}

/** Longer label for tooltips, e.g. "Aug 1, 2026". */
export function formatPerformanceTooltipDate(isoDate: string): string {
  const date = isoToLocalDate(isoDate);
  if (!date) return isoDate;
  return new Intl.DateTimeFormat('en-IN', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

/**
 * Choose evenly spaced point indices for x-axis labels.
 * All underlying points remain plotted; only label density is reduced.
 */
export function buildPerformanceAxisTickIndices(pointCount: number, maxLabels = 16): number[] {
  if (pointCount <= 0) return [];
  if (pointCount === 1) return [0];
  if (pointCount <= maxLabels) {
    return Array.from({ length: pointCount }, (_, index) => index);
  }

  const cappedMax = Math.max(2, maxLabels);
  const step = Math.max(1, Math.ceil((pointCount - 1) / (cappedMax - 1)));
  const indices: number[] = [];
  for (let index = 0; index < pointCount; index += step) {
    indices.push(index);
  }
  const lastIndex = pointCount - 1;
  if (indices[indices.length - 1] !== lastIndex) {
    indices.push(lastIndex);
  }
  return indices;
}

/** Adapt label density to available chart width (px). */
export function maxAxisLabelsForChartWidth(chartWidth: number): number {
  if (chartWidth >= 880) return 16;
  if (chartWidth >= 640) return 12;
  if (chartWidth >= 480) return 8;
  return 6;
}
